import { create } from 'zustand'
import { repo, isMockMode } from '../services/repository'
import { getToken, setToken } from '../api/client'
import type { AuthUser, Language } from '../types/auth'

interface AuthStore {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean

  hydrate: () => Promise<void>
  register: (email: string, password: string, confirmPassword: string) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshMe: () => Promise<void>
  updateLanguage: (language: Language) => Promise<void>
}

export const useAuthStore = create<AuthStore>(set => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,

  hydrate: async () => {
    set({ isLoading: true })
    try {
      if (isMockMode) {
        // Mock 模式不强制登录，允许游客游玩
        if (!getToken()) {
          set({ user: null, isAuthenticated: false, isLoading: false })
          return
        }
        const user = await repo.auth.me()
        set({ user, isAuthenticated: true, isLoading: false })
        return
      }

      if (!getToken()) {
        set({ user: null, isAuthenticated: false, isLoading: false })
        return
      }

      const user = await repo.auth.me()
      set({ user, isAuthenticated: true, isLoading: false })
    } catch {
      setToken(null)
      set({ user: null, isAuthenticated: false, isLoading: false })
    }
  },

  register: async (email, password, confirmPassword) => {
    const { user } = await repo.auth.register({ email, password, confirmPassword })
    set({ user, isAuthenticated: true })
  },

  login: async (email, password) => {
    const { user } = await repo.auth.login({ email, password })
    set({ user, isAuthenticated: true })
  },

  logout: async () => {
    await repo.auth.logout()
    set({ user: null, isAuthenticated: false })
  },

  refreshMe: async () => {
    const user = await repo.auth.me()
    set({ user, isAuthenticated: true })
  },

  updateLanguage: async language => {
    const user = await repo.auth.updateLanguage(language)
    set({ user })
  },
}))
